import { splitByMidnight } from "./lib/dates";
import { useState, useEffect, useRef } from "react";
import React from "react";
import { loadLS, saveLS } from "./lib/storage";
import { sb } from "./lib/supabase";
import { hexToRgb, DEFAULT_THEME_STYLE, loadThemePref, parseThemeStyle, serializeThemeStyle, makeTheme, useIsMobile, ThemeCtx } from "./lib/theme";
import { GlockIcon, NavIcon } from "./components/icons";
import { DialogHost, setDialogTheme } from "./lib/dialog";
import { ADMIN_EMAILS, NAV, LEGAL, GUIDE_VERSION } from "./lib/constants";
import { TacCorners, LiveClock } from "./components/ui";
import { verifyPassword, dbGet, dbSet, dbGetProfile, dbSetProfile, dbGetTimerSec, dbAddTimerSec, dbAddStudySession } from "./lib/db";
import { useInstallApp } from "./lib/pwa";
import { _maintenanceMode, _maintenanceMessage, _maintenanceEta, setMaintenanceMode, setMaintenanceMessage, setMaintenanceEta } from "./lib/maintenance";
import { dbGetConfig, dbUpdateLastSeen } from "./lib/adminDb";
import { useActivityTracker } from "./lib/activity";
import { authPalette, AuthBackground } from "./components/authUi";
import { MfaChallengeScreen } from "./components/mfa";
import { NewsBanner, NotificationBell } from "./components/notifications";
import { AchievementWatcher } from "./components/AchievementWatcher";
import { GlobalStyles } from "./components/GlobalStyles";
import { GuiaUso } from "./components/GuiaUso";
import { FloatingTimer, ExamFloatingTimer } from "./components/FloatingTimer";
import { setFavicon } from "./lib/favicon";
import { Dashboard } from "./screens/Dashboard";
import { Ciclo } from "./screens/Ciclo";
import { BootSequence, AuthScreen, ResetPasswordScreen } from "./screens/Auth";
import { LegalPage, TermsUpdateModal } from "./screens/Legal";
import { MaintenanceScreen } from "./screens/Maintenance";
import { lazyScreen } from "./lib/lazyScreen";

// Telas carregadas sob demanda (Dashboard, Ciclo e telas de entrada continuam no pacote principal;
// o Ciclo precisa ficar sempre montado para o cronômetro).
const Rotina = lazyScreen(() => import("./screens/Rotina"), "Rotina");
const Simulados = lazyScreen(() => import("./screens/Simulados"), "Simulados");
const Comunidade = lazyScreen(() => import("./screens/Comunidade"), "Comunidade");
const Ranking = lazyScreen(() => import("./screens/Ranking"), "Ranking");
const Perfil = lazyScreen(() => import("./screens/Perfil"), "Perfil");
const Questoes = lazyScreen(() => import("./screens/Questoes"), "Questoes");
const EditalVerticalizado = lazyScreen(() => import("./screens/Edital"), "EditalVerticalizado");
const CalendarioEstudos = lazyScreen(() => import("./screens/Calendario"), "CalendarioEstudos");
const AdminPanel = lazyScreen(() => import("./screens/Admin"), "AdminPanel");
const Resumos = lazyScreen(() => import("./screens/Resumos"), "Resumos");
const Flashcards = lazyScreen(() => import("./screens/Flashcards"), "Flashcards");
const Taf = lazyScreen(() => import("./screens/Taf"), "Taf");

// Fundação (fora deste arquivo): lib/storage, supabase, db, timer, edital, theme,
// dialog, format, sound, pwa, constants; components/icons, ui, roles.

// ── APP ──────────────────────────────────────────────────────────────────────

const SCREENS = { dashboard: Dashboard, ranking: Ranking };


function AppMain() {
  const [bootDone,       setBootDone]       = useState(() => {
    try { return sessionStorage.getItem("g17_boot_seen") === "1" || !!loadLS("sf_skipBoot", false); } catch { return false; }
  });
  const [user,           setUser]           = useState(null);
  const [authChecked,    setAuthChecked]    = useState(false);
  const [active,         setActive]         = useState(() => {
    const hash = window.location.hash.replace("#/","").replace("#","").split("?")[0];
    return ['dashboard', 'rotina', 'ciclo', 'simulados', 'comunidade', 'perfil', 'admin', 'calendario', 'edital', 'resumos', 'flashcards'].includes(hash) ? hash : "dashboard";
  });
  const [themeMode,      setThemeMode]      = useState(() => loadLS("sf_themeMode", "dark"));
  const [accentId,       setAccentId]       = useState(() => loadLS("sf_accentId",  "blue"));
  // Estilo visual (Clássico, Tático refinado, Foco calmo ou Institucional) + opções do Institucional
  const [themeStyle,     setThemeStyle]     = useState(() => parseThemeStyle(loadThemePref()).style);
  const [instBase,       setInstBase]       = useState(() => parseThemeStyle(loadThemePref()).instBase);
  const [instMetal,      setInstMetal]      = useState(() => parseThemeStyle(loadThemePref()).instMetal);
  const themeTouchedRef = useRef(false); // vira true só quando a pessoa escolhe a aparência em Perfil
  const chooseThemeStyle = v => { themeTouchedRef.current = true; setThemeStyle(v); };
  const chooseInstBase   = v => { themeTouchedRef.current = true; setInstBase(v); };
  const chooseInstMetal  = v => { themeTouchedRef.current = true; setInstMetal(v); };
  const [cicloMounted,   setCicloMounted]   = useState(false);
  useEffect(() => { if (active === "ciclo") setCicloMounted(true); }, [active]);
  const [moreOpen,       setMoreOpen]       = useState(false);   // menu "Mais" da barra de abas (celular)
  const [guideOpen,      setGuideOpen]      = useState(false);   // guia de uso
  const [termsPending,   setTermsPending]   = useState(false);   // precisa aceitar a versão atual dos Termos/Política
  const legalFromHash = () => { const h = window.location.hash; return h === "#/privacidade" ? "privacidade" : h === "#/termos" ? "termos" : null; };
  const [legalDoc,       setLegalDoc]       = useState(legalFromHash);
  useEffect(() => {
    const on = () => setLegalDoc(legalFromHash());
    window.addEventListener("hashchange", on); window.addEventListener("popstate", on);
    return () => { window.removeEventListener("hashchange", on); window.removeEventListener("popstate", on); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const guideFromRef = useRef(null);                                  // tela em que a pessoa estava ao abrir o guia
  const isMobile = useIsMobile();
  const [routines,       setRoutines]       = useState([]);
  const [simSaved,       setSimSaved]       = useState([]);
  const [simUserContests,setSimUserContests]= useState([]);
  const [projections,    setProjections]    = useState([]);
  const [timerStudiedSec,setTimerStudiedSec]= useState(0);
  const [questEntries,   setQuestEntries]   = useState([]);
  const [targetContest,  setTargetContest]  = useState("");
  const [publicName,     setPublicName]     = useState("Estudante");
  const [avatarUrl,      setAvatarUrl]      = useState("");
  const [userRole,       setUserRole]       = useState("");
  const [dataLoaded,     setDataLoaded]     = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [sideCollapsed, setSideCollapsed] = useState(() => loadLS("sf_sideCollapsed", false));
  const toggleSide = () => setSideCollapsed(v => { saveLS("sf_sideCollapsed", !v); return !v; });
  const [confirmLogout,   setConfirmLogout]   = useState(false);
  const [adminUnlocked,   setAdminUnlocked]   = useState(false);
  const [adminGatePass,   setAdminGatePass]   = useState("");
  const [adminGateErr,    setAdminGateErr]    = useState("");
  const [adminGateLoading,setAdminGateLoading]= useState(false);
  const [isBanned,        setIsBanned]        = useState(false);
  const [communityUnread, setCommunityUnread] = useState(0);
  const communityLastSeenRef = React.useRef(null); // ISO string | null (null = ainda não carregado)

  // Verificação em duas etapas: "checking" | "needed" | "ok". Os dados só carregam com "ok".
  const [mfaState, setMfaState] = useState("checking");
  useEffect(() => {
    if (!user) { setMfaState("checking"); return; }
    setMfaState("checking");
    let alive = true;
    (async () => {
      try {
        const { data, error } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!alive) return;
        if (!error && data && data.nextLevel === "aal2" && data.currentLevel !== "aal2") setMfaState("needed");
        else setMfaState("ok");
      } catch { if (alive) setMfaState("ok"); }
    })();
    return () => { alive = false; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- só ao trocar de conta (não a cada renovação do token)
  const uid = mfaState === "ok" ? user?.id : undefined;
  useActivityTracker(uid, active, isMobile ? "celular" : "computador");   // telas e tempo de uso (aba Atividade do Admin)

  // ── URL routing ──────────────────────────────────────────────
  function navigate(tab) {
    setActive(tab);
    window.history.pushState(null, "", "#/" + tab);
  }
  React.useEffect(() => {
    function onPop() {
      const hash = window.location.hash.replace("#/","").replace("#","");
      const tab = ['dashboard','rotina','ciclo','simulados','comunidade','perfil','admin','calendario','edital','resumos','flashcards'].includes(hash) ? hash : "dashboard";
      setActive(tab);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Load maintenance config from Supabase on app start
  useEffect(() => {
    dbGetConfig().then(cfg => {
      if (cfg.maintenance_mode !== undefined) setMaintenanceMode(cfg.maintenance_mode === "true" || cfg.maintenance_mode === true);
      if (cfg.maintenance_message) setMaintenanceMessage(cfg.maintenance_message);
      if (cfg.maintenance_eta)     setMaintenanceEta(cfg.maintenance_eta);
    });
  }, []);

  // ── Heartbeat — atualiza last_seen_at a cada 60s enquanto logado
  // Também verifica se o perfil ainda existe (detecta exclusão pelo admin)
  useEffect(() => {
    if (!uid) return;
    dbUpdateLastSeen(uid); // imediato ao logar

    async function checkProfile() {
      try {
        const { data, error } = await sb.from("profiles").select("id, banned").eq("id", uid).maybeSingle();
        if (error) return;                       // falha de rede/permissão: não derruba a sessão
        if (!data || data.banned) {              // perfil excluído pelo admin ou conta banida
          await sb.auth.signOut({ scope: "local" });
          return;
        }
        dbUpdateLastSeen(uid);
      } catch {}
    }

    const hb = setInterval(checkProfile, 30_000);
    return () => clearInterval(hb);
  }, [uid]);

  // ── Badge de posts novos na Comunidade ──────────────────────────────────
  // Guarda em "community_state" (Supabase) a última vez que o usuário abriu
  // a aba, pra funcionar entre navegadores/dispositivos. A cada 30s conta
  // quantos posts de OUTRAS pessoas foram criados desde então.
  async function refreshCommunityUnread() {
    if (!uid) return;
    try {
      let lastSeen = communityLastSeenRef.current;
      if (lastSeen === null) {
        const remote = await dbGet("community_state", uid, null);
        lastSeen = remote?.lastSeenAt || new Date(0).toISOString();
        communityLastSeenRef.current = lastSeen;
      }
      const { count, error } = await sb.from("posts")
        .select("id", { count: "exact", head: true })
        .gt("created_at", lastSeen)
        .neq("user_id", uid);
      if (!error) setCommunityUnread(count || 0);
    } catch {}
  }

  useEffect(() => {
    if (!uid) return;
    refreshCommunityUnread();
    const iv = setInterval(refreshCommunityUnread, 30_000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [uid]);

  // Ao abrir a aba Comunidade: zera o contador e marca "visto agora".
  useEffect(() => {
    if (!uid || active !== "comunidade") return;
    const now = new Date().toISOString();
    communityLastSeenRef.current = now;
    setCommunityUnread(0);
    dbSet("community_state", uid, { lastSeenAt: now });
  }, [active, uid]);

  // ── Auth listener
  useEffect(() => {
    sb.auth.getSession().then(({ data }) => {
      if (!showEmailConfirmed) setUser(data.session?.user ?? null);
      setAuthChecked(true);
    });
    const { data: { subscription } } = sb.auth.onAuthStateChange((_e, session) => {
      if (!showEmailConfirmed) setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, []);

  // ── Load all user data from Supabase when user logs in
  useEffect(() => {
    if (!uid) { setDataLoaded(false); return; }
    async function load() {
      const [profile, routinesD, simD, ucD, projD, timerD, questD] = await Promise.all([
        dbGetProfile(uid),
        dbGet("routines",    uid, []),
        dbGet("simulados",   uid, []),
        dbGet("user_contests",uid,[]),
        dbGet("projections", uid, []),
        dbGetTimerSec(uid),
        dbGet("questoes",    uid, []),
      ]);
      if (profile) {
        if (profile.banned) { setIsBanned(true); setDataLoaded(true); return; }
        const name = profile.public_name || "";
        setPublicName(name || "Estudante");
        setTargetContest(profile.target_contest || "");
        setAvatarUrl(profile.avatar_url || "");
        setThemeMode(profile.theme_mode || "dark");
        setAccentId(profile.accent_id || "blue");
        // Escolha feita pela pessoa (gravada na conta) vale; sem escolha, vale o padrão do G17.
        {
          const ps = parseThemeStyle(profile.theme_style || DEFAULT_THEME_STYLE);
          setThemeStyle(ps.style); setInstBase(ps.instBase); setInstMetal(ps.instMetal);
          try {
            if (profile.theme_style) { localStorage.setItem("sf_themeChosen", "1"); saveLS("sf_themeStyle", serializeThemeStyle(ps.style, ps.instBase, ps.instMetal)); }
            else { localStorage.removeItem("sf_themeChosen"); localStorage.removeItem("sf_themeStyle"); }
          } catch {}
        }
        setUserRole(profile.role || "");
      }
      // LGPD: aceite dos Termos/Política. Quem aceitou no cadastro tem o aceite nos metadados da conta.
      if (profile && (profile.terms_version || 0) < LEGAL.version) {
        const meta = user?.user_metadata || {};
        if ((meta.terms_version || 0) >= LEGAL.version) {
          dbSetProfile(uid, { terms_version: LEGAL.version, terms_accepted_at: meta.terms_accepted_at || new Date().toISOString() });
        } else {
          setTermsPending(true);
        }
      }
      // Guia de uso: abre se o perfil ainda não registrou a versão atual
      // (o localStorage evita reabrir caso a gravação no banco tenha falhado).
      if ((profile?.guide_version || 0) < GUIDE_VERSION && loadLS(`sf_guide_${uid}`, 0) < GUIDE_VERSION) setGuideOpen(true);
      setRoutines(routinesD);
      setSimSaved(simD);
      setSimUserContests(ucD);
      setProjections(projD);
      setTimerStudiedSec(timerD);
      setQuestEntries(questD);
      setDataLoaded(true);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [uid]);

  // ── Persist helpers — debounced writes to Supabase
  const debounceRef = useRef({});
  function dbSetDebounced(table, val, delay=800) {
    if (!uid) return;
    clearTimeout(debounceRef.current[table]);
    debounceRef.current[table] = setTimeout(() => dbSet(table, uid, val), delay);
  }

  // ── Watchers: sync state → Supabase
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  useEffect(() => { if (uid && dataLoaded) dbSetDebounced("routines",     routines);        }, [routines]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  useEffect(() => { if (uid && dataLoaded) dbSetDebounced("simulados",    simSaved);        }, [simSaved]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  useEffect(() => { if (uid && dataLoaded) dbSetDebounced("user_contests",simUserContests); }, [simUserContests]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  useEffect(() => { if (uid && dataLoaded) dbSetDebounced("projections",  projections);     }, [projections]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  useEffect(() => { if (uid && dataLoaded) dbSetDebounced("questoes",     questEntries);    }, [questEntries]);

  // Theme persists locally too (instant feedback)
  useEffect(() => {
    saveLS("sf_themeMode", themeMode);
    if (uid) dbSetProfile(uid, { theme_mode: themeMode });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [themeMode]);
  useEffect(() => {
    saveLS("sf_accentId", accentId);
    if (uid) dbSetProfile(uid, { accent_id: accentId });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [accentId]);
  useEffect(() => {
    if (!themeTouchedRef.current) return; // só grava quando a pessoa escolheu
    const v = serializeThemeStyle(themeStyle, instBase, instMetal);
    saveLS("sf_themeStyle", v);
    try { localStorage.setItem("sf_themeChosen", "1"); } catch {}
    if (uid && dataLoaded) dbSetProfile(uid, { theme_style: v });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [themeStyle, instBase, instMetal]);

  // Name / contest / avatar → profile table
  function handleSetPublicName(v) {
    setPublicName(v);
    if (uid) dbSetProfile(uid, { public_name: v });
  }
  function handleSetTargetContest(v) {
    setTargetContest(v);
    if (uid) dbSetProfile(uid, { target_contest: v });
  }
  function handleSetAvatarUrl(v) {
    setAvatarUrl(v);
    if (uid) dbSetProfile(uid, { avatar_url: v });
  }

  // Timer seconds: add and record session
  function handleAddTimerSec(sec) {
    setTimerStudiedSec(p => p + sec);
    if (uid) {
      dbAddTimerSec(uid, sec);
      splitByMidnight(sec).forEach(pt => {   // passou da meia-noite: conta nos dois dias
        dbAddStudySession(uid, pt.sec, pt.at);
        window.dispatchEvent(new CustomEvent("g17-study-added", { detail: { sec: pt.sec, at: pt.at.getTime() } }));   // avisos de sequência/conquista
      });
    }
  }

  function acceptTerms() {
    setTermsPending(false);
    if (uid) dbSetProfile(uid, { terms_version: LEGAL.version, terms_accepted_at: new Date().toISOString() });
  }
  function closeGuide() {
    setGuideOpen(false);
    // volta para a tela em que a pessoa estava (o guia navega pelas telas enquanto explica)
    if (guideFromRef.current) setActive(guideFromRef.current);
    guideFromRef.current = null;
    if (uid) { saveLS(`sf_guide_${uid}`, GUIDE_VERSION); dbSetProfile(uid, { guide_version: GUIDE_VERSION }); }
  }
  useEffect(() => { if (guideOpen && guideFromRef.current === null) guideFromRef.current = active; }, [guideOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  function openGuide() { setMoreOpen(false); setProfileMenuOpen(false); setGuideOpen(true); }
  const installApp = useInstallApp();

  async function handleLogout() {
    await sb.auth.signOut();
    setUser(null); setDataLoaded(false);
    setRoutines([]); setSimSaved([]); setSimUserContests([]);
    setProjections([]); setTimerStudiedSec(0); setQuestEntries([]);
    setPublicName("Estudante"); setTargetContest(""); setAvatarUrl("");
    // Evita que o cache local de ciclos de um usuário vaze pra próxima conta
    // que logar neste mesmo navegador.
    try { localStorage.removeItem("sf_cycles"); localStorage.removeItem("sf_activeCycle"); localStorage.removeItem("sf_cycleStep"); } catch {}
  }

  const canClassic = ADMIN_EMAILS.includes(user?.email);
  const effStyle = (themeStyle === "classico" && !canClassic) ? "institucional" : themeStyle;
  const theme = makeTheme(effStyle, themeMode, accentId, instBase, instMetal);
  setDialogTheme(theme); // diálogos usam o mesmo visual do app
  const t = theme;
  // Ícone da aba do navegador: pistola na cor do tema (bronze institucional se a cor não servir)
  useEffect(() => { setFavicon(theme.ac); }, [theme.ac]);

  // ── Not authenticated
  // Handle email confirmation / password reset redirects
  const [isPasswordReset] = useState(() => window.location.hash.includes("type=recovery"));
  
  // Process recovery token on mount
  const [recoveryProcessed, setRecoveryProcessed] = useState(false);
  const [showEmailConfirmed, setShowEmailConfirmed] = useState(() => {
    const h = window.location.hash;
    return h.includes("type=signup") || h.includes("type=email_change");
  });
  
  useEffect(() => {
    if (isPasswordReset && !recoveryProcessed) {
      // Supabase auto-processes the hash and sets session
      // Just wait a moment for it to process
      const timer = setTimeout(() => {
        setRecoveryProcessed(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isPasswordReset, recoveryProcessed]);

  // Páginas públicas da LGPD (com ou sem login)
  if (legalDoc) {
    return (
      <ThemeCtx.Provider value={theme}>
        <LegalPage doc={legalDoc} onBack={() => { window.location.hash = ""; setLegalDoc(null); }} />
      </ThemeCtx.Provider>
    );
  }

  // Check recovery AFTER session is processed
  if (isPasswordReset) {
    const P = authPalette();
    if (!recoveryProcessed) {
      // Show loading while processing recovery token
      return (
        <div style={{ minHeight:"100vh", background:P.bodyBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ width:40, height:40, border:`3px solid ${P.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      );
    }
    // Now show reset screen (session should be available)
    return <ResetPasswordScreen />;
  }

  if (showEmailConfirmed) {
    const P = authPalette();
    return (
      <>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pop{0%{transform:scale(0.5);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}} @keyframes glow-pulse{0%,100%{filter:drop-shadow(0 0 18px rgba(${P.rgb},0.5))}50%{filter:drop-shadow(0 0 36px rgba(${P.rgb},0.9))}}`}</style>
        <div style={{ position:"fixed", inset:0, background:P.pageBg, zIndex:0 }} />
        <AuthBackground />
        <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:P.fontLayout, padding:"1rem", boxSizing:"border-box", position:"relative", zIndex:1 }}>
          <div style={{ width:"100%", maxWidth:420, background:P.cardBg2, border:`1px solid rgba(${P.rgb},0.2)`, borderRadius:20, padding:isMobile?"2rem 1.4rem":"3rem 2.8rem", textAlign:"center", boxShadow:P.cardShadow2, backdropFilter:"blur(24px)" }}>
            <div style={{ fontSize:"3.5rem", marginBottom:"1.2rem", animation:"pop 0.5s cubic-bezier(.175,.885,.32,1.275) forwards", color:P.ac, display:"flex", justifyContent:"center" }}><NavIcon id="checkcirc" size={56} strokeWidth={1.6} /></div>
            <h2 style={{ color:P.text, fontFamily:P.fontInter, fontWeight:700, fontSize:"1.4rem", letterSpacing:"-0.01em", margin:"0 0 0.6rem" }}>Email confirmado!</h2>
            <p style={{ color:P.dim, fontSize:"0.88rem", margin:"0 0 2rem", lineHeight:1.6 }}>Sua conta está ativa. Faça login para acessar o G17.</p>
            <button onClick={async () => {
              await sb.auth.signOut();
              window.history.replaceState(null, "", window.location.pathname);
              setShowEmailConfirmed(false);
            }}
              style={{ background:P.btnBg, color:P.onAc, border:"none", borderRadius:11, padding:"0.9rem 2.5rem", fontWeight:800, fontSize:"0.97rem", cursor:"pointer", boxShadow:`0 4px 24px rgba(${P.rgb},0.4)`, fontFamily:P.fontBody, letterSpacing:"0.02em" }}>
              Ir para o login →
            </button>
          </div>
        </div>
      </>
    );
  }

  // Vinheta de abertura ("armando o sistema") — uma vez por sessão do navegador,
  // pulada em deep-links de recuperação de senha / confirmação de email.
  if (!bootDone && !isPasswordReset && !showEmailConfirmed) {
    return (
      <BootSequence onDone={() => {
        try { sessionStorage.setItem("g17_boot_seen", "1"); } catch {}
        setBootDone(true);
      }} />
    );
  }

  if (!authChecked) { const P = authPalette(); return (
    <div style={{ minHeight:"100vh", background:P.bodyBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:40, height:40, border:`3px solid ${P.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  ); }

  if (!user) return <AuthScreen onAuth={setUser} />;
  if (mfaState !== "ok") return <MfaChallengeScreen checking={mfaState === "checking"} onDone={() => setMfaState("ok")} onLogout={async () => { await sb.auth.signOut(); setUser(null); }} />;

  // Maintenance mode: user is logged in but not an admin
  if (_maintenanceMode && user && !ADMIN_EMAILS.includes(user.email)) {
    return <MaintenanceScreen message={_maintenanceMessage} eta={_maintenanceEta} />;
  }

  if (!dataLoaded) return (
    <ThemeCtx.Provider value={theme}>
      <div style={{ minHeight:"100vh", background:t.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1rem" }}>
        <div style={{ width:40, height:40, border:`3px solid ${t.ac}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
        <p style={{ color:t.textDim, fontSize:"0.85rem" }}>Carregando seus dados…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </ThemeCtx.Provider>
  );

  // Banned account check
  if (isBanned) {
    return (
      <>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ position:"fixed", inset:0, background:"radial-gradient(ellipse 80% 60% at 50% 0%, #08152a 0%, #030810 60%)", zIndex:0 }} />
        <AuthBackground />
        <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif", position:"relative", zIndex:1, padding:"2rem" }}>
          <div style={{ width:"100%", maxWidth:400, background:"rgba(6,13,26,0.88)", border:"1px solid rgba(248,113,113,0.25)", borderRadius:20, padding:"2.4rem 2.8rem", textAlign:"center", backdropFilter:"blur(24px)", boxShadow:"0 32px 80px rgba(0,0,0,0.7)" }}>
            <div style={{ marginBottom:"1rem", color:"#f87171", display:"flex", justifyContent:"center" }}><NavIcon id="banir" size={44} strokeWidth={1.6} /></div>
            <h2 style={{ margin:"0 0 0.5rem", fontSize:"1.2rem", fontWeight:800, color:"#e8eef0" }}>Conta banida</h2>
            <p style={{ margin:"0 0 1.6rem", fontSize:"0.85rem", color:"#607080", lineHeight:1.6 }}>Esta conta foi suspensa. Entre em contato com o suporte se acredita que isso foi um erro.</p>
            <button onClick={async () => { await sb.auth.signOut(); setUser(null); }}
              style={{ background:"linear-gradient(135deg,#ef4444,#f87171)", color:"#fff", border:"none", borderRadius:11, padding:"0.85rem 2rem", fontWeight:700, fontSize:"0.9rem", cursor:"pointer" }}>
              Voltar ao login
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── App shell
  const [rv, gv, bv] = hexToRgb(t.ac);

  return (
    <ThemeCtx.Provider value={theme}>
      <GlobalStyles />

      {/* ── Ambient background ── */}
      <div style={{ position:"fixed", inset:0, zIndex:0, pointerEvents:"none", overflow:"hidden" }}>
        {/* Base gradient */}
        <div style={{ position:"absolute", inset:0, background: t.ambient && t.mode==="dark"
          ? `radial-gradient(ellipse 120% 80% at 20% 40%, rgba(${rv},${gv},${bv},0.07) 0%, transparent 50%),
             radial-gradient(ellipse 80% 60% at 80% 10%, rgba(${rv},${gv},${bv},0.04) 0%, transparent 45%),
             radial-gradient(ellipse 60% 50% at 60% 90%, rgba(${rv},${gv},${bv},0.03) 0%, transparent 50%),
             ${t.bg}`
          : t.bg }} />
        {t.ambient && t.mode === "dark" && <>
          {/* Orb 1 */}
          <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%", top:"5%", left:"5%",
            background:`radial-gradient(circle, rgba(${rv},${gv},${bv},0.09) 0%, transparent 65%)`,
            animation:"orb-drift-1 20s ease-in-out infinite", filter:"blur(50px)",  }} />
          {/* Orb 2 */}
          <div style={{ position:"absolute", width:450, height:450, borderRadius:"50%", bottom:"8%", right:"8%",
            background:`radial-gradient(circle, rgba(${rv},${gv},${bv},0.06) 0%, transparent 65%)`,
            animation:"orb-drift-2 26s ease-in-out infinite", filter:"blur(60px)",  }} />
          {/* Orb 3 — warm accent */}
          <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", top:"50%", left:"50%",
            background:`radial-gradient(circle, rgba(${rv},${gv},${bv},0.04) 0%, transparent 70%)`,
            animation:"orb-drift-3 32s ease-in-out infinite", filter:"blur(70px)",  }} />
          {/* Scan line */}
          <div style={{ position:"absolute", left:0, right:0, height:"1px",
            background:`linear-gradient(90deg, transparent 0%, rgba(${rv},${gv},${bv},0.1) 20%, rgba(${rv},${gv},${bv},0.3) 50%, rgba(${rv},${gv},${bv},0.1) 80%, transparent 100%)`,
            animation:"scanLine 16s linear infinite", top:0, filter:"blur(0.5px)" }} />
          {/* Subtle grid overlay */}
          <div style={{ position:"absolute", inset:0, opacity:0.018,
            backgroundImage:`linear-gradient(rgba(${rv},${gv},${bv},0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(${rv},${gv},${bv},0.6) 1px, transparent 1px)`,
            backgroundSize:"48px 48px" }} />
        </>}
      </div>

      <div className={`g17-shell g17-style-${t.style}`} style={{ display:"flex", fontFamily:t.fontBody, overflow:"hidden", position:"relative", zIndex:1 }}>

        {/* ── SIDEBAR (no celular vira barra de abas embaixo) ── */}
        <aside className="g17-sidebar" style={{
          width: sideCollapsed ? 76 : 220, flexShrink: 0, display: isMobile ? "none" : "flex", flexDirection:"column",
          transition: "width 0.22s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease",
          background: t.ambient && t.mode==="dark"
            ? `rgba(${Math.round(rv*0.04)},${Math.round(gv*0.04)},${Math.round(bv*0.05)},0.92)`
            : t.bgSide,
          backdropFilter: "blur(32px) saturate(1.8)",
          WebkitBackdropFilter: "blur(32px) saturate(1.8)",
          borderRight: `1px solid rgba(${rv},${gv},${bv},${t.mode==="dark"?0.1:0.12})`,
          boxShadow: t.mode==="dark"
            ? `1px 0 0 rgba(${rv},${gv},${bv},0.06), 8px 0 48px rgba(0,0,0,0.4)`
            : "1px 0 0 rgba(0,0,0,0.06), 4px 0 24px rgba(0,0,0,0.04)",
          padding:"0", zIndex:10, position:"relative",
        }}>
          {/* Top accent line */}
          <div style={{ position:"absolute", top:0, left:0, right:0, height:2,
            background:`linear-gradient(90deg, transparent, rgba(${rv},${gv},${bv},0.6), transparent)`,
            zIndex:1 }} />

          {/* Logo */}
          <div style={{ padding: sideCollapsed ? "1.2rem 0.5rem 0.9rem" : "1.4rem 1.1rem 1.1rem", borderBottom:`1px solid rgba(${rv},${gv},${bv},0.08)` }}>
            <div onClick={()=>navigate("dashboard")} style={{ display:"flex", flexDirection: sideCollapsed ? "column" : "row", alignItems:"center", gap: sideCollapsed ? "0.6rem" : "0.7rem", cursor:"pointer", padding:"0.5rem 0.5rem", borderRadius:12, transition:"background 0.2s ease" }}
              onMouseEnter={e=>e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.08)`}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{ width:38, height:38, borderRadius:11, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", position:"relative",
                background:`linear-gradient(135deg, rgba(${rv},${gv},${bv},0.2), rgba(${rv},${gv},${bv},0.08))`,
                border:`1px solid rgba(${rv},${gv},${bv},0.3)`,
                boxShadow:`0 0 20px rgba(${rv},${gv},${bv},0.2), inset 0 1px 0 rgba(255,255,255,0.08)` }}>
                <TacCorners color={t.ac} size={7} thickness={1.5} inset={-3} opacity={0.65} />
                <GlockIcon size={22} color={t.ac} />
              </div>
              {!sideCollapsed && <div style={{ minWidth:0 }}>
                <p className="g17-logo-text" style={{ margin:0, fontWeight:900, fontSize:"1rem",
                  fontFamily:"'Barlow Condensed','Outfit',sans-serif", textTransform:"uppercase",
                  letterSpacing:"0.04em", lineHeight:1.1 }}>G17 App</p>
                <div style={{ display:"flex", alignItems:"center", gap:"0.32rem", marginTop:"0.22rem" }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 6px #4ade80", flexShrink:0, animation:"pulse 2.4s ease-in-out infinite" }} />
                  <p className="g17-mono" style={{ margin:0, fontSize:"0.5rem", color:t.textFaint, letterSpacing:"0.15em",
                    textTransform:"uppercase", fontWeight:600 }}>Sistema Online</p>
                </div>
              </div>}
              <div style={{ marginLeft: sideCollapsed ? 0 : "auto", flexShrink:0 }} onClick={e => e.stopPropagation()}>
                <NotificationBell userId={uid} userCreatedAt={user?.created_at} />
              </div>
            </div>
          </div>

          {/* Nav label */}
          <div style={{ padding: sideCollapsed ? "0.8rem 0 0.4rem" : "1rem 1.4rem 0.5rem", textAlign: sideCollapsed ? "center" : "left" }}>
            <span style={{ fontSize:"0.55rem", fontWeight:700, letterSpacing:"0.22em", textTransform:"uppercase", color:t.textFaint, opacity:0.7 }}>Menu</span>
          </div>

          {/* Nav items */}
          <nav style={{ flex:1, padding: sideCollapsed ? "0 0.7rem" : "0 0.6rem", display:"flex", flexDirection:"column", gap:"0.08rem" }}>
            {NAV.map((item, i) => {
              const isActive = active === item.id;
              return (
                <button key={item.id} onClick={(e) => {
                  const btn = e.currentTarget;
                  const rect = btn.getBoundingClientRect();
                  const rip = document.createElement("span");
                  rip.className = "nav-ripple active";
                  rip.style.cssText = `position:absolute;border-radius:50%;background:rgba(${rv},${gv},${bv},0.25);width:10px;height:10px;left:${e.clientX-rect.left-5}px;top:${e.clientY-rect.top-5}px;transform:scale(0);opacity:0.5;pointer-events:none;animation:nav-ripple 0.55s cubic-bezier(0.16,1,0.3,1) forwards;`;
                  btn.appendChild(rip);
                  setTimeout(() => rip.remove(), 600);
                  navigate(item.id);
                }}
                  className={"g17-nav-item" + (isActive ? " active" : "")}
                  title={sideCollapsed ? item.label : undefined} aria-label={sideCollapsed ? item.label : undefined}
                  style={{
                    width:"100%", display:"flex", alignItems:"center", gap:"0.65rem", justifyContent: sideCollapsed ? "center" : "flex-start",
                    padding: sideCollapsed ? "0.7rem 0" : "0.62rem 0.85rem 0.62rem 1.1rem", borderRadius:11, border:"none",
                    background: isActive
                      ? `linear-gradient(135deg, rgba(${rv},${gv},${bv},0.18) 0%, rgba(${rv},${gv},${bv},0.08) 100%)`
                      : "transparent",
                    color: isActive ? t.ac : t.textDim,
                    fontSize:"0.84rem", fontWeight: isActive ? 700 : 500,
                    cursor:"pointer", textAlign:"left", position:"relative",
                    boxShadow: isActive ? `inset 0 1px 0 rgba(${rv},${gv},${bv},0.15), 0 4px 16px rgba(${rv},${gv},${bv},0.1)` : "none",
                  }}>
                  <div style={{ position:"absolute", left:0, top:"20%", bottom:"20%", width:3, borderRadius:"0 3px 3px 0",
                    background: isActive ? t.ac : "transparent",
                    boxShadow: isActive ? `0 0 12px ${t.ac}` : "none",
                    transition:"all 0.28s cubic-bezier(0.16,1,0.3,1)" }} />
                  <span style={{ fontSize:"1.05rem", lineHeight:1, flexShrink:0, width:20, textAlign:"center",
                    filter: isActive ? `drop-shadow(0 0 8px rgba(${rv},${gv},${bv},0.9))` : "none",
                    opacity: isActive ? 1 : 0.5,
                    transition:"all 0.22s ease",
                    transform: isActive ? "scale(1.12)" : "scale(1)" }}>{t.style === "classico" ? item.icon : <NavIcon id={item.id} size={18} />}</span>
                  {!sideCollapsed && <span style={{ flex:1 }}>{item.label}</span>}
                  {item.id === "comunidade" && communityUnread > 0 && (
                    <span style={{ ...(sideCollapsed ? { position:"absolute", top:4, right:8 } : {}), minWidth:16, height:16, borderRadius:99, background: isActive ? t.ac : "#ef4444", color: isActive ? (t.mode==="dark"?"#000":"#fff") : "#fff", fontSize:"0.6rem", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px", flexShrink:0, boxShadow: isActive ? `0 0 6px ${t.ac}` : "0 0 6px #ef4444" }}>
                      {communityUnread > 99 ? "99+" : communityUnread}
                    </span>
                  )}
                  {isActive && !sideCollapsed && (
                    <span style={{ width:6, height:6, borderRadius:"50%", background:t.ac, flexShrink:0,
                      boxShadow:`0 0 10px ${t.ac}, 0 0 20px rgba(${rv},${gv},${bv},0.5)`,
                      animation:"pulse 2s ease-in-out infinite" }} />
                  )}
                </button>
              );
            })}
            {(() => {
              const isAdm = ADMIN_EMAILS.includes(user?.email);
              const bottom = [
                ...(installApp.can ? [{ key:"instalar", label:"Instalar app", onClick:installApp.install, icon:<NavIcon id="baixar" size={18} /> }] : []),
                ...(isAdm ? [{ key:"admin", label:"Admin", onClick:() => navigate("admin"), icon:<NavIcon id="admin" size={18} />, admin:true }] : []),
                { key:"guia", label:"Guia de uso", onClick:openGuide, icon: t.style === "classico" ? "?" : <NavIcon id="guia" size={18} /> },
                { key:"recolher", label: sideCollapsed ? "Expandir menu" : "Recolher menu", onClick:toggleSide, icon:<NavIcon id={sideCollapsed ? "expandir" : "recolher"} size={18} /> },
              ];
              return bottom.map((b, bi) => {
                const on = b.admin && active === "admin";
                return (
                  <button key={b.key} onClick={b.onClick} className={"g17-nav-item" + (on ? " active" : "")} title={sideCollapsed ? b.label : undefined} aria-label={b.label} aria-current={on ? "page" : undefined}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.65rem", justifyContent: sideCollapsed ? "center" : "flex-start",
                      padding: sideCollapsed ? "0.7rem 0" : "0.62rem 0.85rem 0.62rem 1.1rem", borderRadius:11,
                      border: b.admin ? `1px solid rgba(${rv},${gv},${bv},${on ? 0.55 : 0.3})` : "none",
                      background: b.admin ? `rgba(${rv},${gv},${bv},${on ? 0.18 : 0.08})` : "transparent",
                      color: b.admin ? t.ac : t.textDim, fontSize:"0.84rem", fontWeight: b.admin ? 700 : 500, cursor:"pointer", textAlign:"left", position:"relative",
                      marginTop: bi === 0 ? "auto" : (b.admin ? 0 : "0.1rem"), marginBottom: b.key === "recolher" ? "0.5rem" : (b.admin ? "0.25rem" : 0) }}>
                    <span style={{ fontSize:"1.05rem", lineHeight:1, flexShrink:0, width:20, textAlign:"center", opacity: b.admin ? 1 : 0.5, display:"flex", justifyContent:"center" }}>{b.icon}</span>
                    {!sideCollapsed && <span style={{ flex:1 }}>{b.label}</span>}
                  </button>
                );
              });
            })()}
          </nav>

          {/* Social links */}
          <div style={{ padding: sideCollapsed ? "0.75rem 0.4rem 0.5rem" : "0.75rem 1.1rem 0.5rem", borderTop:`1px solid rgba(${rv},${gv},${bv},0.07)` }}>
            <div style={{ display:"flex", alignItems:"center", gap:"0.45rem", justifyContent:"center", marginBottom:"0.4rem" }}>
              <a href="https://mepagaumcafe.com.br/p/g17" target="_blank" rel="noopener noreferrer"
                style={{ position:"relative", display:"inline-flex", alignItems:"center", justifyContent:"center", width:32, height:32, borderRadius:9,
                  background:`rgba(${rv},${gv},${bv},0.07)`, border:`1px solid rgba(${rv},${gv},${bv},0.12)`,
                  cursor:"pointer", textDecoration:"none", fontSize:"1rem", transition:"all 0.22s", flexShrink:0 }}
                onMouseEnter={e=>{ e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.2)`; e.currentTarget.style.transform="scale(1.14) translateY(-2px)"; e.currentTarget.style.boxShadow=`0 6px 18px rgba(${rv},${gv},${bv},0.3)`; }}
                onMouseLeave={e=>{ e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.07)`; e.currentTarget.style.transform="scale(1) translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>
                <span style={{ animation:"heartbeat 1.6s ease-in-out infinite", display:"flex", color:t.ac }}><NavIcon id="apoio" size={17} /></span>
                <span className="social-tooltip" style={{ position:"absolute", bottom:"calc(100% + 7px)", left:"50%", transform:"translateX(-50%) translateY(0)", background:"rgba(0,0,0,0.92)", color:"#fff", fontSize:"0.63rem", fontWeight:600, padding:"0.25rem 0.55rem", borderRadius:7, whiteSpace:"nowrap", pointerEvents:"none", opacity:0, zIndex:999 }}>Apoie o G17! ❤️</span>
              </a>
            </div>
            {!sideCollapsed && <>
            <p style={{ margin:0, textAlign:"center", fontSize:"0.52rem", color:t.textFaint, letterSpacing:"0.05em" }}>© 2026 G17 App. Todos os direitos reservados.</p>
            <p style={{ margin:"0.35rem 0 0", textAlign:"center", fontSize:"0.62rem" }}>
              <a href="#/termos" style={{ color:t.textDim }}>Termos</a><span style={{ color:t.textFaint, margin:"0 0.35rem" }}>·</span><a href="#/privacidade" style={{ color:t.textDim }}>Privacidade</a>
            </p>
            </>}
          </div>

          {/* Profile footer */}
          <div style={{ padding: sideCollapsed ? "0.8rem 0.5rem" : "0.8rem 0.9rem", borderTop:`1px solid rgba(${rv},${gv},${bv},0.1)`, position:"relative" }}>
            {profileMenuOpen && (
              <>
                <div style={{ position:"fixed", inset:0, zIndex:998 }} onClick={()=>setProfileMenuOpen(false)} />
                <div className="g17-modal" style={{ position:"absolute", bottom:"calc(100% + 8px)", left:"0.7rem", right: sideCollapsed ? "auto" : "0.7rem", width: sideCollapsed ? 210 : "auto",
                  background: t.mode==="dark" ? `rgba(6,10,20,0.97)` : t.bgCard,
                  border:`1px solid rgba(${rv},${gv},${bv},0.2)`, borderRadius:14, overflow:"hidden",
                  boxShadow:`0 -20px 56px rgba(0,0,0,0.6), 0 0 0 1px rgba(${rv},${gv},${bv},0.06)`,
                  backdropFilter:"blur(32px)", zIndex:999 }}>
                  <div style={{ height:2, background:`linear-gradient(90deg, transparent, rgba(${rv},${gv},${bv},0.5), transparent)` }} />
                  {[
                    { icon:"config", label:"Configurações", action:()=>{navigate("perfil");setProfileMenuOpen(false);}, red:false },
                    { icon:"guia", label:"Guia de uso",   action:openGuide, red:false },
                    { icon:"sair",  label:"Sair",          action:()=>{ setProfileMenuOpen(false); setConfirmLogout(true); }, red:true },
                  ].map((item,i) => (
                    <button key={i} onClick={item.action}
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.75rem",
                        padding:"0.75rem 1rem", background:"transparent", border:"none",
                        borderBottom: i<2 ? `1px solid rgba(${rv},${gv},${bv},0.08)` : "none",
                        color: item.red ? "#f87171" : t.textMid, fontSize:"0.83rem", cursor:"pointer",
                        textAlign:"left", transition:"background 0.15s, padding-left 0.18s",
                        fontFamily:"'Outfit',sans-serif", fontWeight:500 }}
                      onMouseEnter={e=>{ e.currentTarget.style.background=item.red?"rgba(248,113,113,0.08)":`rgba(${rv},${gv},${bv},0.07)`; e.currentTarget.style.paddingLeft="1.25rem"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; e.currentTarget.style.paddingLeft="1rem"; }}>
                      <NavIcon id={item.icon} size={16} /> {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div title={sideCollapsed ? publicName : undefined} style={{ display:"flex", alignItems:"center", justifyContent: sideCollapsed ? "center" : "flex-start", gap:"0.65rem", padding:"0.45rem 0.4rem",
              borderRadius:11, cursor:"pointer", transition:"background 0.2s",
              background: profileMenuOpen ? `rgba(${rv},${gv},${bv},0.1)` : "transparent" }}
              onClick={()=>setProfileMenuOpen(p=>!p)}
              onMouseEnter={e=>{ if(!profileMenuOpen) e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.07)`; }}
              onMouseLeave={e=>{ if(!profileMenuOpen) e.currentTarget.style.background="transparent"; }}>
              <div className="g17-avatar-ring" style={{ width:34, height:34, borderRadius:"50%", flexShrink:0,
                background:`linear-gradient(135deg, rgba(${rv},${gv},${bv},0.2), rgba(${rv},${gv},${bv},0.08))`,
                border:`2px solid ${profileMenuOpen ? t.ac : `rgba(${rv},${gv},${bv},0.35)`}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"0.82rem", color:t.ac, fontWeight:800, overflow:"hidden",
                boxShadow: profileMenuOpen ? `0 0 18px rgba(${rv},${gv},${bv},0.5)` : "none",
                transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)" }}>
                {avatarUrl ? <img src={avatarUrl} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="" /> : publicName?.[0]?.toUpperCase()||"?"}
              </div>
              {!sideCollapsed && <>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ margin:0, fontSize:"0.8rem", color:t.textMid, fontWeight:700, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{publicName}</p>
                <p style={{ margin:0, fontSize:"0.62rem", color:t.textFaint, marginTop:"0.05rem" }}>Ver perfil</p>
              </div>
              <span style={{ fontSize:"0.6rem", color:t.textFaint,
                transform: profileMenuOpen ? "rotate(0deg)" : "rotate(180deg)",
                transition:"transform 0.28s cubic-bezier(0.16,1,0.3,1)" }}>▲</span>
              </>}
            </div>
          </div>
        </aside>

        {/* ── Logout modal ── */}
        {termsPending && <TermsUpdateModal user={user} onAccept={acceptTerms} onLogout={() => { setTermsPending(false); handleLogout(); }} />}
        {uid && <AchievementWatcher uid={uid} isAdmin={ADMIN_EMAILS.includes(user?.email)} ready={dataLoaded} timerStudiedSec={timerStudiedSec} simSaved={simSaved} questEntries={questEntries} screen={active} />}
        {guideOpen && !termsPending && <GuiaUso onClose={closeGuide} onGo={setActive} isAdmin={ADMIN_EMAILS.includes(user?.email)} />}
        {confirmLogout && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", backdropFilter:"blur(8px)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}
            onClick={()=>setConfirmLogout(false)}>
            <div onClick={e=>e.stopPropagation()} className="g17-modal" style={{
              background: t.mode==="dark" ? `rgba(${Math.round(rv*0.07)},${Math.round(gv*0.07)},${Math.round(bv*0.07)},0.96)` : t.bgCard,
              border:`1px solid rgba(${rv},${gv},${bv},0.2)`, borderRadius:18, padding:"2rem 1.8rem", width:"100%", maxWidth:320,
              boxShadow:`0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(${rv},${gv},${bv},0.08)`, backdropFilter:"blur(20px)",
            }}>
              <div style={{ width:48, height:48, borderRadius:"50%", background:"rgba(248,113,113,0.15)", border:"2px solid rgba(248,113,113,0.4)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem", color:"#f87171" }}><NavIcon id="sair" size={22} /></div>
              <p style={{ margin:"0 0 0.4rem", fontSize:"1rem", fontWeight:800, color:t.text }}>Sair da conta?</p>
              <p style={{ margin:"0 0 1.6rem", fontSize:"0.85rem", color:t.textDim, lineHeight:1.5 }}>Você precisará fazer login novamente para acessar o G17.</p>
              <div style={{ display:"flex", gap:"0.75rem" }}>
                <button onClick={()=>setConfirmLogout(false)}
                  style={{ flex:1, padding:"0.7rem", borderRadius:10, border:`1px solid rgba(${rv},${gv},${bv},0.2)`, background:"transparent", color:t.textMid, fontSize:"0.85rem", fontWeight:600, cursor:"pointer", transition:"all 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.08)`}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  Cancelar
                </button>
                <button onClick={()=>{ setConfirmLogout(false); handleLogout(); }}
                  style={{ flex:1, padding:"0.7rem", borderRadius:10, border:"none", background:"linear-gradient(135deg,#ef4444,#f87171)", color:"#fff", fontSize:"0.85rem", fontWeight:700, cursor:"pointer", boxShadow:"0 4px 16px rgba(239,68,68,0.35)" }}>
                  Sair
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Main ── */}
        <main style={{ flex:1, minWidth:0, overflowY:"auto", overflowX:"hidden", padding: isMobile ? "0 1rem calc(5.5rem + env(safe-area-inset-bottom))" : "2.2rem 2.8rem", position:"relative", background:"transparent", overscrollBehavior:"contain" }}>
          {/* ── Topo compacto (celular): logo, notificações e avatar ── */}
          {isMobile && (
            <div style={{ position:"sticky", top:0, zIndex:20, margin:"0 -1rem 1rem", padding:"0.6rem 1rem", display:"flex", alignItems:"center", gap:"0.6rem",
              background:t.bgSide, borderBottom:`1px solid ${t.border}` }}>
              <button onClick={()=>navigate("dashboard")} aria-label="Ir para o início"
                style={{ display:"flex", alignItems:"center", gap:"0.55rem", background:"transparent", border:"none", padding:0, cursor:"pointer", color:t.text }}>
                <span style={{ width:32, height:32, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", background:t.acDim, border:`1px solid ${t.acBorder}` }}>
                  <GlockIcon size={19} color={t.ac} />
                </span>
                <span style={{ fontFamily:t.fontDisplay, fontWeight:800, fontSize:"1.05rem", letterSpacing:"0.04em", textTransform:"uppercase" }}>G17 App</span>
              </button>
              <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:"0.5rem" }}>
                <NotificationBell userId={uid} userCreatedAt={user?.created_at} />
                <button onClick={()=>navigate("perfil")} aria-label="Abrir perfil"
                  style={{ width:36, height:36, borderRadius:"50%", overflow:"hidden", border:`2px solid ${t.acBorder}`, background:t.bgInner, color:t.ac, fontWeight:800, fontSize:"0.85rem", cursor:"pointer", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {avatarUrl ? <img src={avatarUrl} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="" /> : publicName?.[0]?.toUpperCase()||"?"}
                </button>
              </div>
            </div>
          )}
          {/* Top edge glow */}
          <div style={{ position:"absolute", top:0, left:0, right:0, height:"1px",
            background:`linear-gradient(90deg, transparent, rgba(${rv},${gv},${bv},0.25), rgba(${rv},${gv},${bv},0.1), transparent)`,
            pointerEvents:"none" }} />
          {/* Subtle vignette on left */}
          <div style={{ position:"absolute", top:0, left:0, bottom:0, width:40, pointerEvents:"none",
            background:`linear-gradient(90deg, rgba(${rv},${gv},${bv},0.02), transparent)` }} />

          {/* ── Barra de status tática (topo; no celular o topo compacto a substitui) ── */}
          {!isMobile && <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.75rem",
            marginBottom:"1.6rem", paddingBottom:"0.9rem", borderBottom:`1px solid ${t.ambient ? `rgba(${rv},${gv},${bv},0.1)` : t.borderIn}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:"0.7rem" }}>
              <div>
                {t.style !== "foco" && <p className="g17-mono" style={{ margin:0, fontSize:"0.62rem", letterSpacing:"0.16em", color:t.textFaint, textTransform:"uppercase", fontWeight:600 }}>G17 // Painel</p>}
                <p style={{ margin:0, fontSize:"0.95rem", fontWeight:800, color:t.text, letterSpacing:"-0.01em" }}>
                  {([...NAV, { id:"admin", icon:"⚡", label:"Admin" }].find(n=>n.id===active) || {}).label || "Painel"}
                </p>
              </div>
            </div>
            {t.style !== "foco" && <div style={{ display:"flex", alignItems:"center", gap:"0.9rem" }}>
              <LiveClock color={t.textDim} />
              <div style={{ width:1, height:16, background:t.border }} />
              <div style={{ display:"flex", alignItems:"center", gap:"0.35rem" }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 8px #4ade80", flexShrink:0, animation:"pulse 2.2s ease-in-out infinite" }} />
                <span className="g17-mono" style={{ fontSize:"0.62rem", letterSpacing:"0.1em", color:t.textFaint, textTransform:"uppercase", fontWeight:600 }}>Sistema Online</span>
              </div>
            </div>}
          </div>}

          <div key={active} className="g17-page">
          <React.Suspense fallback={<div style={{ padding:"3rem 1rem", textAlign:"center", color:t.textDim, fontSize:"0.9rem" }}>Carregando…</div>}>
          { active === "perfil"
            ? <Perfil themeMode={themeMode} accentId={accentId} setThemeMode={setThemeMode} setAccentId={setAccentId}
                themeStyle={themeStyle} setThemeStyle={chooseThemeStyle} instBase={instBase} setInstBase={chooseInstBase} instMetal={instMetal} setInstMetal={chooseInstMetal}
                publicName={publicName} setPublicName={handleSetPublicName}
                avatarUrl={avatarUrl}   setAvatarUrl={handleSetAvatarUrl}
                simCount={simSaved.length} totalStudiedSec={timerStudiedSec}
                simSaved={simSaved} questEntries={questEntries}
                targetContest={targetContest} setTargetContest={handleSetTargetContest}
                user={user} onLogout={handleLogout} userRole={userRole} />
            : active === "rotina"
            ? <Rotina routines={routines} setRoutines={setRoutines} />
            : active === "ciclo"
            ? null
            : active === "questoes"
            ? <Questoes questEntries={questEntries} setQuestEntries={setQuestEntries} />
            : active === "simulados"
            ? <Simulados saved={simSaved} setSaved={setSimSaved}
                userContests={simUserContests} setUserContests={setSimUserContests}
                projections={projections} setProjections={setProjections} uid={uid} />
            : (() => {
                const Screen = SCREENS[active];
                return active==="dashboard"
                  ? <><NewsBanner userId={uid} userCreatedAt={user?.created_at} /><Dashboard routines={routines} cycles={[]} simSaved={simSaved} projections={projections}
                      timerStudiedSec={timerStudiedSec} publicName={publicName} questEntries={questEntries} currentUserId={uid} adminEmail={user?.email} /></>
                  : active === "comunidade" ? <Comunidade currentUser={user} publicName={publicName} avatarUrl={avatarUrl} adminEmail={user?.email} />
                  : active === "ranking" ? <Ranking currentUserId={uid} adminEmail={user?.email} />
                  : active === "edital" ? <EditalVerticalizado uid={uid} isAdmin={ADMIN_EMAILS.includes(user?.email)} />
                  : active === "resumos" ? <Resumos uid={uid} />
                  : active === "flashcards" ? <Flashcards uid={uid} />
                  : active === "taf" ? <Taf uid={uid} />
                  : active === "calendario" ? <CalendarioEstudos uid={uid} isAdmin={ADMIN_EMAILS.includes(user?.email)} />
                  : active === "admin" ? (
                    adminUnlocked ? <AdminPanel currentUser={user} /> : (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
                        <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:16, padding:"2.5rem 2rem", width:"100%", maxWidth:360, boxShadow: t.mode==="dark" ? "0 8px 24px rgba(0,0,0,0.28)" : "0 8px 24px rgba(20,24,40,0.08)", textAlign:"center" }}>
                          <div style={{ width:64, height:64, borderRadius:18, background:t.acDim, border:`1px solid ${t.acBorder}`, color:t.ac, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem" }}><NavIcon id="cadeado" size={30} /></div>
                          <p style={{ margin:"0 0 0.3rem", fontSize:"1rem", fontWeight:800, color:t.text }}>Área Restrita</p>
                          <p style={{ margin:"0 0 1.5rem", fontSize:"0.82rem", color:t.textDim }}>Digite sua senha para acessar o painel admin.</p>
                          <input type="password" value={adminGatePass} onChange={e=>{setAdminGatePass(e.target.value);setAdminGateErr("");}}
                            onKeyDown={async e=>{
                              if(e.key==="Enter"){
                                setAdminGateLoading(true);
                                const {error}=await verifyPassword(user.email, adminGatePass);
                                if(error){setAdminGateErr("Senha incorreta.");}else{setAdminUnlocked(true);}
                                setAdminGateLoading(false);
                              }
                            }}
                            placeholder="Senha de administrador"
                            style={{ width:"100%", background:t.bgInner, border:`1px solid ${t.border}`, borderRadius:10, padding:"0.75rem 1rem", color:t.text, fontSize:"0.88rem", outline:"none", boxSizing:"border-box", marginBottom:"0.5rem" }} autoFocus />
                          {adminGateErr && <p style={{ margin:"0 0 0.5rem", fontSize:"0.8rem", color:"#f87171" }}>{adminGateErr}</p>}
                          <button
                            onClick={async()=>{
                              setAdminGateLoading(true);
                              const {error}=await verifyPassword(user.email, adminGatePass);
                              if(error){setAdminGateErr("Senha incorreta.");}else{setAdminUnlocked(true);}
                              setAdminGateLoading(false);
                            }}
                            disabled={!adminGatePass||adminGateLoading}
                            style={{ width:"100%", background:t.ac, border:"none", borderRadius:10, padding:"0.75rem", color:t.onAc || (t.mode==="dark"?"#000":"#fff"), fontWeight:700, fontSize:"0.88rem", cursor:adminGatePass?"pointer":"not-allowed", opacity:adminGatePass?1:0.5 }}>
                            {adminGateLoading ? "Verificando…" : "Entrar"}
                          </button>
                        </div>
                      </div>
                    )
                  )
                  : <Screen />;
              })()
          }
          </React.Suspense>
          </div>
          {/* Ciclo fica montado depois da primeira visita: assim o cronômetro continua em outras abas */}
          {(cicloMounted || active === "ciclo") && (
            <div className={active === "ciclo" ? "g17-page" : undefined} style={{ display: active === "ciclo" ? "block" : "none" }}>
              <Ciclo routines={routines} onAddStudiedSec={()=>{}} onAddTimerSec={handleAddTimerSec} uid={uid} dataLoaded={dataLoaded} isAdmin={ADMIN_EMAILS.includes(user?.email)} />
            </div>
          )}
        </main>
        {active !== "ciclo" && <FloatingTimer onOpen={() => navigate("ciclo")} />}
        <ExamFloatingTimer onOpen={() => navigate("simulados")} stacked={active !== "ciclo"} />

        {/* ── Barra de abas (só no celular) ── */}
        {isMobile && (() => {
          const isAdmin = ADMIN_EMAILS.includes(user?.email);
          const tabs = [
            { id:"dashboard",  label:"Início" },
            { id:"ciclo",      label:"Ciclo" },
            { id:"simulados",  label:"Simulados" },
            { id:"calendario", label:"Calendário" },
          ];
          const moreItems = [
            { id:"rotina", label:"Rotina" },
            { id:"edital", label:"Edital verticalizado" },
            { id:"resumos", label:"Meus resumos" },
            { id:"flashcards", label:"Flashcards" },
            { id:"taf", label:"TAF" },
            { id:"comunidade", label:"Comunidade" },
            { id:"perfil", label:"Perfil e aparência" },
            ...(isAdmin ? [{ id:"admin", label:"Admin" }] : []),
          ];
          const moreActive = !tabs.some(x => x.id === active);
          const tabBtn = (id, label, isOn, onClick, badge) => (
            <button key={id} onClick={onClick} aria-current={isOn ? "page" : undefined}
              style={{ position:"relative", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, minHeight:52,
                background:"transparent", border:"none", cursor:"pointer", color: isOn ? t.ac : t.textDim, fontSize:"0.68rem", fontWeight: isOn ? 700 : 500, padding:0 }}>
              <NavIcon id={id} size={22} />
              <span>{label}</span>
              {badge > 0 && <span style={{ position:"absolute", top:4, left:"calc(50% + 6px)", minWidth:16, height:16, borderRadius:8, background:"#ef4444", color:"#fff", fontSize:"0.6rem", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>{badge > 99 ? "99+" : badge}</span>}
            </button>
          );
          return (
            <>
              {moreOpen && (
                <div onClick={()=>setMoreOpen(false)} style={{ position:"fixed", inset:0, zIndex:59, background:"rgba(0,0,0,0.55)" }}>
                  <div onClick={e=>e.stopPropagation()} role="dialog" aria-label="Mais opções"
                    style={{ position:"absolute", left:0, right:0, bottom:0, background:t.bgCard, borderTop:`1px solid ${t.border}`, borderRadius:"16px 16px 0 0",
                      padding:"0.6rem 0.75rem calc(5.4rem + env(safe-area-inset-bottom))", boxShadow:"0 -16px 40px rgba(0,0,0,0.4)" }}>
                    <div style={{ width:40, height:4, borderRadius:2, background:t.border, margin:"0 auto 0.6rem" }} />
                    {moreItems.map(it => (
                      <button key={it.id} onClick={()=>{ setMoreOpen(false); navigate(it.id); }}
                        style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10,
                          background: active===it.id ? t.acDim : "transparent", border:"none", color: active===it.id ? t.ac : t.text, fontSize:"0.95rem", fontWeight:600, cursor:"pointer", textAlign:"left" }}>
                        <NavIcon id={it.id} size={20} />{it.label}
                        {it.id === "comunidade" && communityUnread > 0 && <span style={{ marginLeft:"auto", minWidth:20, height:20, padding:"0 6px", borderRadius:10, background:"#ef4444", color:"#fff", fontSize:"0.7rem", fontWeight:800, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>{communityUnread > 99 ? "99+" : communityUnread}</span>}
                      </button>
                    ))}
                    {installApp.can && (
                      <button onClick={()=>{ setMoreOpen(false); installApp.install(); }}
                        style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10, background:"transparent", border:"none", color:t.ac, fontSize:"0.95rem", fontWeight:700, cursor:"pointer", textAlign:"left" }}>
                        <NavIcon id="baixar" size={20} />Instalar app
                      </button>
                    )}
                    <button onClick={openGuide}
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10, background:"transparent", border:"none", color:t.text, fontSize:"0.95rem", fontWeight:600, cursor:"pointer", textAlign:"left" }}>
                      <NavIcon id="guia" size={20} />Guia de uso
                    </button>
                    <a href="#/privacidade" onClick={()=>setMoreOpen(false)}
                      style={{ display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10, color:t.text, fontSize:"0.95rem", fontWeight:600, textDecoration:"none" }}>
                      <NavIcon id="admin" size={20} />Privacidade e termos
                    </a>
                    <a href="https://mepagaumcafe.com.br/p/g17" target="_blank" rel="noopener noreferrer"
                      style={{ display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10, color:t.text, fontSize:"0.95rem", fontWeight:600, textDecoration:"none" }}>
                      <NavIcon id="apoio" size={20} />Apoie o G17
                    </a>
                    <button onClick={()=>{ setMoreOpen(false); setConfirmLogout(true); }}
                      style={{ width:"100%", display:"flex", alignItems:"center", gap:"0.85rem", minHeight:52, padding:"0 0.75rem", borderRadius:10, background:"transparent", border:"none", color:"#f87171", fontSize:"0.95rem", fontWeight:600, cursor:"pointer", textAlign:"left" }}>
                      <NavIcon id="sair" size={20} />Sair
                    </button>
                  </div>
                </div>
              )}
              <nav className="g17-tabbar" aria-label="Menu principal"
                style={{ position:"fixed", left:0, right:0, bottom:0, zIndex:60, gridTemplateColumns:"repeat(5, minmax(0, 1fr))",
                  padding:"0.25rem 0.25rem calc(0.35rem + env(safe-area-inset-bottom))", background:t.bgSide, borderTop:`1px solid ${t.border}` }}>
                {tabs.map(x => tabBtn(x.id, x.label, active === x.id && !moreOpen, () => { setMoreOpen(false); navigate(x.id); }, 0))}
                {tabBtn("mais", "Mais", moreActive || moreOpen, () => setMoreOpen(o => !o), communityUnread)}
              </nav>
            </>
          );
        })()}
      </div>
    </ThemeCtx.Provider>
  );
}

export default function App() {
  return <><AppMain /><DialogHost /></>;
}
